import { match, P } from '@st/pattern'
import { CaretDownIcon, DockToLeftIcon } from '@st/theme'
import { useState, type ReactNode } from 'react'

export type SidebarVisibility = 'expanded' | 'collapsed'

/**
 * A feature's right sidebar. The viewer owns the frame, the titled header and
 * the hide/show toggle; the feature supplies the title, frame style and the
 * content (tabs, scrolling body and footer via `ViewerSidebarContent`).
 */
export type ViewerSidebarPanel = {
  title: string
  content: ReactNode
  widthClassName?: string
  className?: string
}

/** The header shares the viewer toolbar's 50px row so both columns line up. */
export function ViewerSidebarFrame({
  panel: { title, content, widthClassName = 'w-72', className = 'bg-white' },
  onHide
}: {
  panel: ViewerSidebarPanel
  onHide: () => void
}) {
  return (
    <aside
      className={`isolate flex min-h-0 shrink-0 flex-col overflow-hidden border-l border-gray-200 ${widthClassName} ${className}`}
    >
      <div className="flex h-[50px] shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-gray-100 px-5">
        <h2 className="min-w-0 truncate text-sm font-semibold text-gray-900">{title}</h2>
        <ViewerSidebarToggle expanded onToggle={onHide} />
      </div>
      {content}
    </aside>
  )
}

export function ViewerSidebarContent({
  children,
  tabs,
  footer
}: {
  children: ReactNode
  tabs?: ReactNode
  footer?: ReactNode
}) {
  return (
    <>
      {tabs}
      <div className="min-h-0 flex-1 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
        {children}
      </div>
      {footer && <div className="shrink-0 border-t border-gray-200 bg-white p-4">{footer}</div>}
    </>
  )
}

export function ViewerSidebarToggle({
  expanded,
  onToggle
}: {
  expanded: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      aria-label={match(expanded)
        .with(true, () => 'Hide sidebar')
        .with(false, () => 'Show sidebar')
        .exhaustive()}
      aria-expanded={expanded}
      className="flex size-9 shrink-0 items-center justify-center rounded text-[#545666] outline-none hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      onClick={onToggle}
    >
      <DockToLeftIcon className="size-6" />
    </button>
  )
}

/** Controlled selection lets editors keep their existing unsaved-change policy. */
export function ViewerSidebarTabs<T extends string>({
  tabs,
  selected,
  onSelect,
  disabled = false
}: {
  tabs: readonly { id: T; label: string }[]
  selected: T
  onSelect: (id: T) => void
  disabled?: boolean
}) {
  return (
    <div
      role="tablist"
      aria-label="Document sidebar"
      className="flex shrink-0 border-b border-gray-200 bg-white shadow-sm"
    >
      {tabs.map(({ id, label }) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={selected === id}
          disabled={disabled}
          className={`h-12 flex-1 border-b-2 border-r border-r-gray-200 px-4 text-base font-semibold last:border-r-0 disabled:opacity-50 ${match(
            selected === id
          )
            .with(true, () => 'border-b-blue-500 text-gray-900')
            .with(false, () => 'border-b-transparent text-gray-500 hover:bg-gray-100')
            .exhaustive()}`}
          onClick={() => onSelect(id)}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

export function ViewerSidebarToolbar({
  ariaLabel,
  children
}: {
  ariaLabel: string
  children: ReactNode
}) {
  return (
    <div role="toolbar" aria-label={ariaLabel} className="flex items-center gap-1 p-2">
      {children}
    </div>
  )
}

export function ViewerSidebarBody({ children }: { children: ReactNode }) {
  return <div className="border-t border-stone-200">{children}</div>
}

export function ViewerSidebarSection({ label, children }: { label: string; children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <section className="border-b border-gray-200 bg-gray-100">
      <button
        type="button"
        className="sticky top-0 z-20 flex h-12 w-full items-center justify-between gap-3 bg-white px-5 py-3 text-left text-base font-medium uppercase tracking-wide text-gray-900 shadow-sm hover:bg-gray-100"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((value) => !value)}
      >
        <span className="min-w-0 truncate">{label}</span>
        <CaretDownIcon
          className={`size-4 shrink-0 text-gray-600 ${match(collapsed)
            .with(true, () => 'rotate-0')
            .with(false, () => 'rotate-180')
            .exhaustive()}`}
        />
      </button>
      {match(collapsed)
        .with(true, () => null)
        .with(false, () => children)
        .exhaustive()}
    </section>
  )
}

export function ViewerSidebarRow({
  label,
  depth = 0,
  action,
  children
}: {
  label?: ReactNode
  depth?: number
  action?: ReactNode
  children?: ReactNode
}) {
  return (
    <div
      className="border-t border-gray-200 bg-gray-100 py-4 pr-6"
      style={{ paddingLeft: 24 + depth * 16 }}
    >
      {match({ action, label })
        .with({ action: P.when(Boolean) }, () => (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1 break-words text-base font-normal text-gray-600">
              {label}
            </div>
            {action}
          </div>
        ))
        .with({ label: P.when(Boolean) }, () => (
          <div className="break-words text-base font-normal text-gray-600">{label}</div>
        ))
        .otherwise(() => null)}
      {children}
    </div>
  )
}

export function ViewerSidebarValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mt-3 min-w-0">
      <div className="text-base font-normal text-gray-600">{label}</div>
      <div className="mt-1 break-words text-lg font-semibold text-gray-900">{children}</div>
    </div>
  )
}
